import { HtsButtonIcon, HtsTypography, HtslIcon } from '@hts/shared/ui';
import { getLuminance } from '@hts/shared/utils';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { IGetStaticElementsResponse } from '../../../shared/types';
import { ItemType } from '../../../shared/types/general';
import styles from '../../styles/NotificationBar/NotificationBar.module.scss';

type NotificationBarItems = IGetStaticElementsResponse['notificationBars'];
type NotificationBarItem = ItemType<NotificationBarItems>;

interface INotificationBarProps {
  items: NotificationBarItems;
}

export const NotificationBar: React.FC<INotificationBarProps> = memo(({ items }) => {
  const contentContainerRef = useRef<HTMLDivElement>(null);
  const sliderRef = useRef<HTMLDivElement>(null);

  const shouldHideArrows = items.length < 2;

  const [isOneLineContent, setOneLineContent] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  const activeColor = items.at(currentIndex)?.barColor ?? '#fff';
  const activeLuminance = getLuminance(activeColor);
  const activeLuminanceColor = activeLuminance > 0.5 ? 'black' : 'white';

  useEffect(() => {
    const observeTarget = contentContainerRef.current;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.target === observeTarget) {
          const newHeight = entry.target?.clientHeight;

          const slider = sliderRef.current;

          if (slider && slider.children[currentIndex]) {
            slider.style.height = `${slider.children[currentIndex].clientHeight}px`;
          }

          setOneLineContent(newHeight === 51);
        }
      }
    });

    if (observeTarget) {
      resizeObserver.observe(observeTarget);
    }

    return () => {
      if (observeTarget) {
        resizeObserver.unobserve(observeTarget);
      }

      resizeObserver.disconnect();
    };
  }, [currentIndex]);

  const onPrevArrow = useCallback(() => {
    setCurrentIndex((prev) => (prev === 0 ? items.length - 1 : prev - 1));
  }, [items.length]);

  const onNextArrow = useCallback(() => {
    setCurrentIndex((prev) => (prev === items.length - 1 ? 0 : prev + 1));
  }, [items.length]);

  const renderNotification = useCallback(
    ({ id, barText, barButton, link, barColor }: NotificationBarItem, index: number) => (
      <HtsTypography
        variant="body2"
        fontWeight="500"
        className={styles.sliderItem}
        key={`${id}-${index}`}
        style={{
          translate: `${-100 * currentIndex}%`,
          color: activeLuminanceColor,
        }}
      >
        {barText}&nbsp;
        <Link to={link} style={{ textDecoration: 'underline', textWrap: 'nowrap' }}>
          <span>{barButton}</span>
        </Link>
      </HtsTypography>
    ),
    [currentIndex, activeLuminanceColor]
  );

  return (
    <div className={styles.container} style={{ background: activeColor }}>
      <div
        className={`${styles.contentContainer} ${isOneLineContent ? styles.oneLineContainer : styles.manyLinesContainer}`}
        ref={contentContainerRef}
      >
        <HtsButtonIcon
          className={styles.arrowButton}
          hidden={shouldHideArrows}
          color={activeLuminanceColor}
          onClick={onPrevArrow}
        >
          <HtslIcon icon="keyboard-arrow-left" className={styles.arrowIcon} />
        </HtsButtonIcon>

        <div className={styles.slider} ref={sliderRef}>
          {items.map(renderNotification)}
        </div>

        <HtsButtonIcon
          className={styles.arrowButton}
          hidden={shouldHideArrows}
          color={activeLuminanceColor}
          onClick={onNextArrow}
        >
          <HtslIcon icon="keyboard-arrow-right" className={styles.arrowIcon} />
        </HtsButtonIcon>
      </div>
    </div>
  );
});
